import { type ReactNode, useId } from "react";
import { Text } from "@/components/Text";

/**
 * Card-free admin building blocks (docs/design/web/WEB_ADMIN_DESIGN.md "Page patterns").
 * Every admin section composes these instead of Cards.
 */

/** A titled plain section (a region named by its heading): heading + optional description, then content. */
export function PageSection({
  title,
  description,
  actions,
  children,
}: Readonly<{ title: string; description?: string; actions?: ReactNode; children: ReactNode }>) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <Text variant="h3" id={headingId}>
            {title}
          </Text>
          {description && (
            <Text variant="body-sm" tone="secondary">
              {description}
            </Text>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Divided list of SettingsRows. */
export function SettingsList({ children }: Readonly<{ children: ReactNode }>) {
  return <div className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">{children}</div>;
}

/** Label + description left, control right (stacks on phones). */
export function SettingsRow({
  label,
  description,
  badge,
  children,
}: Readonly<{ label: string; description?: string; badge?: ReactNode; children: ReactNode }>) {
  return (
    <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between md:gap-8">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <Text as="span" variant="body">
            {label}
          </Text>
          {badge}
        </span>
        {description && (
          <Text variant="body-sm" tone="tertiary">
            {description}
          </Text>
        )}
      </div>
      <div className="w-full shrink-0 md:w-72">{children}</div>
    </div>
  );
}

export interface Stat {
  label: string;
  value: ReactNode;
  hint?: string;
}

/** Compact stat row (big number + caption) instead of stat cards. */
export function StatRow({ stats }: Readonly<{ stats: Stat[] }>) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:flex md:flex-wrap md:gap-x-12">
      {stats.map((stat) => (
        <div key={stat.label} className="flex flex-col gap-0.5">
          <dt>
            <Text as="span" variant="caption" tone="tertiary">
              {stat.label}
            </Text>
          </dt>
          <dd>
            <Text as="span" variant="h2">
              {stat.value}
            </Text>
          </dd>
          {stat.hint && (
            <dd>
              <Text as="span" variant="caption" tone="tertiary">
                {stat.hint}
              </Text>
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}

/** Read-only definition rows (label: value), two columns on desktop. */
export function DefinitionList({ items }: Readonly<{ items: Array<{ label: string; value: ReactNode }> }>) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-0.5">
          <dt>
            <Text as="span" variant="caption" tone="tertiary">
              {item.label}
            </Text>
          </dt>
          <dd>
            <Text as="span" variant="body-sm">
              {item.value}
            </Text>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Bottom-of-page destructive actions (kill switch, delete model). */
export function DangerZone({
  title,
  description,
  children,
}: Readonly<{ title: string; description: string; children: ReactNode }>) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-error-subtle p-4 md:p-6">
      <Text variant="h3" tone="error">
        {title}
      </Text>
      <Text variant="body-sm" tone="secondary">
        {description}
      </Text>
      {children}
    </section>
  );
}

// Breakdown bars deliberately echo the brand (the old UI's orange→purple bars); `tone` varies
// them between neighbouring breakdowns so the page isn't one colour.
const BAR_TONE = {
  brand: "bg-brand-gradient",
  primary: "bg-brand-primary",
  secondary: "bg-brand-secondary",
} as const;

/** Horizontal bar with label + value, for simple breakdowns (usage by team/model, costs). */
export function BarRow({
  label,
  value,
  fraction,
  tone = "brand",
}: Readonly<{ label: string; value: string; fraction: number; tone?: keyof typeof BAR_TONE }>) {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <Text as="span" variant="body-sm" className="truncate">
          {label}
        </Text>
        <Text as="span" variant="body-sm" tone="secondary" className="shrink-0 tabular-nums">
          {value}
        </Text>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-secondary" aria-hidden="true">
        <div className={`h-full rounded-full ${BAR_TONE[tone]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
